import { lazy } from 'react'

import { type IBreadcrumbsProps } from '@webapp/core/components/Breadcrumbs'
import { Page } from '@webapp/core/components/Page'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'

const QuickBooksDesktopViewChunk = lazy(() =>
  import('../../feature-sets/QuickBooksDesktopView').then(
    ({ QuickBooksDesktopView }) => ({
      default: QuickBooksDesktopView,
    })
  )
)

export const QuickBooksDesktopSettingsTabs = {
  GENERAL: 'general',
  IMPORT_EXPORT: 'import-export',
  ACTIVITY_LOG: 'activity-log',
  ERROR_LOG: 'error-log',
} as const

export type QuickBooksDesktopSettingsTab =
  (typeof QuickBooksDesktopSettingsTabs)[keyof typeof QuickBooksDesktopSettingsTabs]

export const QuickBooksDesktopFieldMappingModels = {
  PO: 'po',
  BILLS: 'bills',
} as const

export type QuickBooksDesktopFieldMappingModel =
  (typeof QuickBooksDesktopFieldMappingModels)[keyof typeof QuickBooksDesktopFieldMappingModels]

export const QuickBooksDesktopImportStatuses = {
  UNIMPORTED: 'unimported',
  IMPORTED: 'imported',
} as const

export type QuickBooksDesktopImportStatus =
  (typeof QuickBooksDesktopImportStatuses)[keyof typeof QuickBooksDesktopImportStatuses]

export const QuickBooksDesktopExportResources = {
  PURCHASE_ORDERS: 'purchase-orders',
  ITEM_RECEIPTS: 'item-receipts',
  BILLS: 'bills',
} as const

export type QuickBooksDesktopExportResource =
  (typeof QuickBooksDesktopExportResources)[keyof typeof QuickBooksDesktopExportResources]

export const QuickBooksDesktopExportStatuses = {
  EXPORTABLE: 'exportable',
  EXPORTED: 'exported',
  ARCHIVED: 'archived',
} as const

export type QuickBooksDesktopExportStatus =
  (typeof QuickBooksDesktopExportStatuses)[keyof typeof QuickBooksDesktopExportStatuses]

export interface QuickBooksDesktopSettingsPageParams {
  tab: QuickBooksDesktopSettingsTab
}

export interface QuickBooksDesktopFieldMappingPageParams {
  model: QuickBooksDesktopFieldMappingModel
}

export interface QuickBooksDesktopAccountsPageParams {
  status: QuickBooksDesktopImportStatus
}

export interface QuickBooksDesktopVendorsPageParams {
  status: QuickBooksDesktopImportStatus
}

export interface QuickBooksDesktopExportsPageParams {
  resource: QuickBooksDesktopExportResource
  status: QuickBooksDesktopExportStatus
}

export const QuickBooksDesktopPage = () => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()

  const pageTitle = intl.formatMessage(messages.pageTitle)

  const breadcrumbs = [
    {
      link: url('SettingsPage'),
      name: intl.formatMessage(messages.settingsTitle),
    },
    {
      link: url('IntegrationsPage'),
      name: intl.formatMessage(messages.integrationsTitle),
    },
    {
      name: pageTitle,
      escapeCaseFormatting: true,
    },
  ] satisfies IBreadcrumbsProps['crumbs']

  return (
    <Page pageTitle={pageTitle} breadcrumbs={breadcrumbs}>
      <QuickBooksDesktopViewChunk />
    </Page>
  )
}
